/**
 * One decision, framed (spec §2.1).
 *
 * Every step that asks a question renders inside this: chapter label ·
 * headline · why-sentence · the control · Back / Continue. It owns the three
 * rules the whole stepper leans on, so no step can get them wrong:
 *
 *  1. **Continue is never disabled.** A step hands over `validate()`; a click
 *     with something missing FOCUSES the control and shows the sentence —
 *     under the button here, and at the control in the step body (which reads
 *     it from `useStepReason()`). The sentence clears the moment the value
 *     becomes valid, so the operator never has to dismiss it.
 *  2. **The keyboard works.** Enter submits from any field (TEXTAREA, BUTTON
 *     and anything under `[data-no-enter]` keep their own Enter; a branded
 *     Select that opted into `enterSubmits` is the one button that submits —
 *     see the handler), and
 *     ⌘[ / Alt+← goes Back.
 *  3. **The control is focused on entry.** The first `[data-autofocus]` in the
 *     body gets focus when the step mounts — which is also what makes Back
 *     restore focus to the control you came from, since the shell keys this
 *     component by step id.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useMinWidth } from "../../lib/useIsMobile";
import { GhostNumeral, HEADLINE, MONO_LABEL, REASON, SUBCOPY } from "./ui";

/** The sentence the current step is showing, for the body to repeat AT the
 *  control. Context rather than a prop: `StepFrame` owns the state, and a
 *  body that renders the same sentence twice from two sources would drift. */
const ReasonContext = createContext<string | null>(null);

export function useStepReason(): string | null {
  return useContext(ReasonContext);
}

/** The id a step body puts on its inline sentence, and the control points at
 *  with `aria-describedby` — one id, because one step asks one thing. */
export const REASON_ID = "ql-step-reason";

export interface StepFrameProps {
  /** "CAPTIONS" — the chapter this step belongs to. */
  chapter: string;
  /** Position among the lane's DECISIONS, or null for an uncounted screen. */
  n: number | null;
  total: number | null;
  headline: string;
  /** "" renders nothing — `reading` and `done` have no why-sentence. */
  why: string;
  children: ReactNode;
  continueLabel?: string;
  continueIcon?: ReactNode;
  /** null ⇒ the step may advance; else the sentence AND what to focus. */
  validate: () => null | { reason: string; focus: HTMLElement | null };
  onContinue: () => void;
  /** null ⇒ no Back button (welcome has nothing behind it; reading asks). */
  onBack: (() => void) | null;
  /** A spinner in the button while the step is working. */
  busy?: boolean;
  /** false on a step that owns Enter itself. Default true. */
  enterAdvances?: boolean;
  /**
   * A sentence to show the moment the step OPENS, rather than on the first
   * Continue — how a failure that happened somewhere else (a Create the
   * server refused) arrives at the field that owns it (spec §8). It clears
   * itself as soon as `validate()` passes, like any other reason.
   */
  initialReason?: string | null;
}

export function StepFrame(p: StepFrameProps): React.JSX.Element {
  const wide = useMinWidth(900);
  const [reason, setReason] = useState<string | null>(p.initialReason ?? null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // The handlers are registered once; they must never close over a stale
  // `validate` (it is a fresh closure over the state on every render).
  const latest = useRef(p);
  latest.current = p;

  const submit = (): void => {
    const verdict = latest.current.validate();
    if (verdict) {
      setReason(verdict.reason);
      verdict.focus?.focus();
      // On a phone the step is a scrolling region under the sample, so the
      // control we just focused can be out of sight — and a sentence pointing
      // at something you cannot see is not a reason.
      verdict.focus?.scrollIntoView?.({ block: "center" });
      return;
    }
    setReason(null);
    latest.current.onContinue();
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;

  // Focus the control on entry. The shell keys this component by step id, so
  // "on mount" and "on arriving at the step" are the same moment — including
  // arriving backwards, which is how Back restores focus to the control.
  useEffect(() => {
    // `preventScroll`: entering a step must not move the page. On a phone the
    // step sits UNDER the sample, and a plain focus() scrolled the top bar and
    // half the phone off the screen.
    bodyRef.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus({ preventScroll: true });
  }, []);

  // No dependency array on purpose: the sentence has to disappear the moment
  // the value becomes valid, and "valid" is whatever `validate()` says on the
  // render that just happened.
  useEffect(() => {
    if (reason && latest.current.validate() === null) setReason(null);
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey && e.key === "[") || (e.altKey && e.key === "ArrowLeft")) {
        const back = latest.current.onBack;
        if (back) {
          e.preventDefault();
          back();
        }
        return;
      }
      if (e.key !== "Enter" || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // A modified Enter is left alone everywhere EXCEPT in a textarea: plain
      // Enter is a line break there, so ⌘/Ctrl+Enter is the one way to
      // continue the step from the keyboard.
      const modified = e.metaKey || e.ctrlKey;
      if (modified && tag !== "TEXTAREA") return;
      if (latest.current.enterAdvances === false) return;
      const closest = (sel: string): boolean =>
        !!target && typeof target.closest === "function" && !!target.closest(sel);
      // The Select protocol (ui/Select.tsx `enterSubmits`): Return on a CLOSED
      // dropdown is "this is my answer" — the native select advanced the step
      // and Continue advertises ↵. The trigger is a BUTTON that has already
      // called preventDefault (to swallow its own click, so the list does not
      // open), so both of the exemptions below would wrongly eat it; skip
      // them for that one control. While the list is OPEN, focus is in the
      // listbox — not the trigger — and Enter picks a row and stops
      // propagating, so it never arrives here.
      const viaSelect = closest("[data-enter-submits]");
      if (!viaSelect) {
        if (e.defaultPrevented) return;
        // A button owns its own Enter (that IS the click); a textarea owns its
        // newline (but not ⌘/Ctrl+Enter — see above); `data-no-enter` is how
        // a field says "Return means something here" (the link input adds the
        // link).
        if ((tag === "TEXTAREA" && !modified) || tag === "BUTTON") return;
        if (closest("[data-no-enter]")) return;
      }
      e.preventDefault();
      submitRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const buttons = (
    <div style={{ display: "grid", gap: 8, justifyItems: wide ? "end" : "stretch" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: wide ? "flex-end" : "stretch" }}>
        {p.onBack && (
          <button
            type="button"
            onClick={p.onBack}
            title="Back (⌘[)"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "13px 18px",
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,.10)",
              background: "transparent",
              color: "var(--ql-fg-muted)",
              fontSize: 13.5,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={15} />
            Back
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            flex: wide ? undefined : 1,
            padding: "15px 30px",
            borderRadius: 16,
            border: "none",
            fontWeight: 800,
            fontSize: 15,
            fontFamily: "inherit",
            color: "#000",
            background: "linear-gradient(135deg,#FFD500,#FFA500)",
            boxShadow: "0 4px 16px rgba(255,213,0,.30)",
            cursor: "pointer",
            transition: "all .2s var(--ql-ease)",
          }}
        >
          {p.busy ? <Loader2 size={16} className="ql-spin" /> : (p.continueIcon ?? <ArrowRight size={16} />)}
          {p.continueLabel ?? "Continue"}
          <span aria-hidden style={{ fontSize: 13, opacity: 0.5 }}>↵</span>
        </button>
      </div>
      {/* One announcement for the two copies: this one is the live region, and
          the step body's copy is what `aria-describedby` points the control at
          (`REASON_ID`), so a screen reader hears it once on appearance and
          once more only if you go back to the field. */}
      {/* MUTED on purpose: the step body prints the same sentence AT the
          control in alarm red, and §1.3 asks this one to be passive. Two
          identical red sentences read as shouting (QA 2026-09-16, m12). */}
      {reason && (
        <div
          role="alert"
          style={{ ...REASON, color: "var(--ql-fg-muted)", fontWeight: 500, textAlign: wide ? "right" : "left" }}
        >
          {reason}
        </div>
      )}
    </div>
  );

  return (
    <ReasonContext.Provider value={reason}>
      <div style={{ display: "grid", gap: wide ? 26 : 20, paddingBottom: wide ? 0 : 108 }}>
        <StepHeader chapter={p.chapter} n={p.n} total={p.total} headline={p.headline} why={p.why} />
        <div ref={bodyRef} style={{ minWidth: 0 }}>
          {p.children}
        </div>
        {wide && buttons}
      </div>
      {!wide && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            padding: "12px 16px",
            paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
            background: "rgba(10,10,11,.94)",
            backdropFilter: "blur(12px)",
            borderTop: "1px solid rgba(255,255,255,.07)",
          }}
        >
          {buttons}
        </div>
      )}
    </ReasonContext.Provider>
  );
}

/** The three lines every screen opens with — shared with the two screens that
 *  are not questions (`welcome`, `reading`) so they look like the rest. */
export function StepHeader(props: {
  chapter: string;
  n: number | null;
  total: number | null;
  headline: string;
  why: string;
}): React.JSX.Element {
  const { chapter, n, total, headline, why } = props;
  return (
    <div style={{ position: "relative" }}>
      {n !== null && <GhostNumeral n={n} />}
      <div style={{ position: "relative", display: "grid", gap: 12 }}>
        <span style={MONO_LABEL}>
          {chapter}
          {n !== null && total !== null && (
            <>
              {" · "}
              <span style={{ color: "var(--ql-yellow)" }}>{String(n).padStart(2, "0")}</span>
            </>
          )}
        </span>
        <h1 style={HEADLINE}>{headline}</h1>
        {why && <p style={SUBCOPY}>{why}</p>}
      </div>
    </div>
  );
}
